/**
 * Search results page with full search functionality.
 * 
 * Features:
 * - Display full search results with highlighted snippets
 * - Filter by category, tag, status
 * - Pagination
 * - Sort options
 * - URL query parameter support
 * 
 * Route: /search?q=query&category=&tag=&status=
 * 
 * Called by:
 * - SearchBar "See all results" link
 * - Direct navigation
 */
import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Typography,
  Input,
  Select,
  Card,
  Space,
  Empty,
  Spin,
  Pagination,
  Row,
  Col,
} from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { searchEssays, getCategories, getTags } from '../services/api';
import StatusBadge from '../components/StatusBadge';
import type { EssayStatus } from '@compendium/shared';

const { Title, Text, Paragraph } = Typography;

// =============================================================================
// Constants
// =============================================================================

const PAGE_SIZE = 10;

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'in_review', label: 'In Review' },
  { value: 'revision', label: 'Revision' },
  { value: 'approved', label: 'Approved' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

// =============================================================================
// Main Component
// =============================================================================

/**
 * Search results page component.
 * Displays full search results with filtering and pagination.
 */
function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [localQuery, setLocalQuery] = useState(searchParams.get('q') || '');

  // Get filter values from URL
  const query = searchParams.get('q') || '';
  const categoryFilter = searchParams.get('category') || '';
  const tagFilter = searchParams.get('tag') || '';
  const statusFilter = searchParams.get('status') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);

  // Sync local query with URL
  useEffect(() => {
    setLocalQuery(query);
  }, [query]);

  // Fetch categories for filter dropdown
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
  });

  // Fetch tags for filter dropdown
  const { data: tags = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: getTags,
  });

  // Search query
  const { data: searchResponse, isLoading, isError } = useQuery({
    queryKey: ['search', query, categoryFilter, tagFilter, statusFilter, page],
    queryFn: () =>
      searchEssays(query, {
        category: categoryFilter || undefined,
        tag: tagFilter || undefined,
        status: (statusFilter as EssayStatus) || undefined,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      }),
    enabled: query.length >= 2,
  });

  const results = searchResponse?.results || [];
  const totalResults = searchResponse?.total || 0;

  /**
   * Update URL params and trigger search.
   */
  function updateParams(updates: Record<string, string>) {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    // Reset page when filters change
    if (!updates.page) {
      newParams.delete('page');
    }
    setSearchParams(newParams);
  }

  /**
   * Handle search form submission.
   */
  function handleSearch() {
    if (localQuery.trim()) {
      updateParams({ q: localQuery.trim() });
    }
  }

  // Category options for filter
  const categoryOptions = [
    { value: '', label: 'All categories' },
    ...categories.map((cat) => ({ value: cat.slug, label: cat.name })),
  ];

  // Tag options for filter
  const tagOptions = [
    { value: '', label: 'All tags' },
    ...tags.map((tag) => ({ value: tag.slug, label: tag.name })),
  ];

  return (
    <div style={{ padding: '24px 0' }}>
      {/* Search header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Search Essays</Title>

        {/* Search input */}
        <Input.Search
          placeholder="Search essays..."
          value={localQuery}
          onChange={(e) => setLocalQuery(e.target.value)}
          onSearch={handleSearch}
          enterButton
          size="large"
          style={{ maxWidth: 600, marginBottom: 16 }}
        />

        {/* Filters */}
        <Row gutter={16} style={{ maxWidth: 800 }}>
          <Col xs={24} sm={8}>
            <Select
              placeholder="Category"
              value={categoryFilter || undefined}
              onChange={(value) => updateParams({ category: value })}
              options={categoryOptions}
              style={{ width: '100%' }}
              allowClear
            />
          </Col>
          <Col xs={24} sm={8}>
            <Select
              placeholder="Tag"
              value={tagFilter || undefined}
              onChange={(value) => updateParams({ tag: value })}
              options={tagOptions}
              style={{ width: '100%' }}
              allowClear
            />
          </Col>
          <Col xs={24} sm={8}>
            <Select
              placeholder="Status"
              value={statusFilter || undefined}
              onChange={(value) => updateParams({ status: value })}
              options={STATUS_OPTIONS}
              style={{ width: '100%' }}
              allowClear
            />
          </Col>
        </Row>
      </div>

      {/* Results count */}
      {query && !isLoading && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          {totalResults} result{totalResults !== 1 ? 's' : ''} for "{query}"
          {categoryFilter && ` in category "${categoryFilter}"`}
          {tagFilter && ` with tag "${tagFilter}"`}
          {statusFilter && ` with status "${statusFilter}"`}
        </Text>
      )}

      {/* Loading state */}
      {isLoading && (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      )}

      {/* Error state */}
      {isError && (
        <Empty
          description="Search failed. Please try again."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}

      {/* No query */}
      {!query && !isLoading && (
        <Empty
          description="Enter a search query to find essays"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}

      {/* No results */}
      {query && !isLoading && !isError && results.length === 0 && (
        <Empty
          description={`No results found for "${query}"`}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}

      {/* Results list */}
      {!isLoading && results.length > 0 && (
        <div>
          {results.map((result) => (
            <Card
              key={result.essay.id}
              style={{
                marginBottom: 16,
                background: '#1a1a1a',
                border: '1px solid #333',
              }}
              hoverable
            >
              <Link to={`/reviewer/essays/${result.essay.slug}`}>
                <Space align="start" style={{ width: '100%' }}>
                  <FileTextOutlined style={{ fontSize: 24, color: '#8c8c8c', marginTop: 4 }} />
                  <div style={{ flex: 1 }}>
                    {/* Title with highlights */}
                    <Title level={5} style={{ marginBottom: 8 }}>
                      <span
                        dangerouslySetInnerHTML={{ __html: result.titleSnippet }}
                      />
                    </Title>

                    {/* Content snippet with highlights */}
                    <Paragraph
                      type="secondary"
                      style={{ marginBottom: 8, fontSize: 14 }}
                    >
                      <span
                        dangerouslySetInnerHTML={{ __html: result.contentSnippet }}
                      />
                    </Paragraph>

                    {/* Meta info */}
                    <Space>
                      <StatusBadge status={result.essay.status} />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Updated {new Date(result.essay.updatedAt).toLocaleDateString()}
                      </Text>
                    </Space>
                  </div>
                </Space>
              </Link>
            </Card>
          ))}

          {/* Pagination */}
          {totalResults > PAGE_SIZE && (
            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <Pagination
                current={page}
                total={totalResults}
                pageSize={PAGE_SIZE}
                onChange={(newPage) => updateParams({ page: newPage.toString() })}
                showSizeChanger={false}
              />
            </div>
          )}
        </div>
      )}

      {/* Styles for highlighted text */}
      <style>{`
        mark {
          background: #fadb14;
          color: #000;
          padding: 0 2px;
          border-radius: 2px;
        }
      `}</style>
    </div>
  );
}

export default SearchResultsPage;
